import React, { useState } from 'react';
import { XMarkIcon, StarIcon } from '@heroicons/react/24/outline';
import { storage } from '../../config';
// import { ref, push } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import axios from 'axios';
import { showToast } from '../CustomToast';

interface ReviewsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ReviewsModal({ isOpen, onClose }: ReviewsModalProps) {
    const [newReview, setNewReview] = useState({
        content: '',
        writer: '',
        value: 5,
        selfie: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [rating, setRating] = useState(0);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (file.size <= 10 * 1024 * 1024) {
                setSelectedFile(file);
            } else {
                showToast.warning('File size must be less than 10MB');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newReview.writer || !newReview.content || !selectedFile || rating === 0) {
            showToast.warning('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        try {
            // Upload selfie to Storage
            const selfieRef = storageRef(storage, `reviews/${selectedFile.name}-${Date.now()}`);
            await uploadBytes(selfieRef, selectedFile);
            const selfieUrl = await getDownloadURL(selfieRef);

            // Prepare data for API
            const data = {
                writer: newReview.writer,
                content: newReview.content,
                value: rating,
                selfie: selfieUrl
            };

            // Send data to API (which will handle Firebase saving)
            await axios.post(
                "https://api.betallsoftware.com/ron/save-review",
                data,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "a7XjV9QrNpBlF6xQ2mR9DdE1HtW8zLoN3Sa5GeJ3KwRfP5RtZ6Xa3IbD8Jk5Lz2Nv4JmTdXsQp9XtMjVuRnWbV3FrUhLm1"
                    }
                }
            );

            // Reset form
            setNewReview({
                content: '',
                writer: '',
                value: 5,
                selfie: ''
            });
            setSelectedFile(null);
            setRating(0);
            onClose(); // Close modal after successful submission

            showToast.success('Review submitted successfully!');
        } catch (error) {
            console.error('Error submitting review:', error);
            showToast.error('Failed to submit review. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>

                <h1 className="text-3xl font-bold mb-6">You can leave reviews here</h1>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            Full Name: <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={newReview.writer}
                            onChange={(e) => setNewReview(prev => ({ ...prev, writer: e.target.value }))}
                            className="w-full border rounded p-2"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            Please upload your selfie photo <span className="text-red-500">*</span>
                        </label>
                        <div className="border-2 border-dashed rounded-lg p-6 text-center">
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/gif"
                                onChange={handleFileChange}
                                className="hidden"
                                id="selfie-upload"
                                required
                            />
                            <label htmlFor="selfie-upload" className="cursor-pointer">
                                <div className="flex flex-col items-center">
                                    <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span className="text-indigo-600">Upload a file</span>
                                    <span className="text-gray-500 text-sm">or drag and drop</span>
                                    <span className="text-gray-400 text-xs mt-1">PNG, JPG, GIF up to 10MB</span>
                                </div>
                            </label>
                            {selectedFile && (
                                <div className="mt-2 text-sm text-gray-600">
                                    Selected file: {selectedFile.name}
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            Please write review: <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={newReview.content}
                            onChange={(e) => setNewReview(prev => ({ ...prev, content: e.target.value }))}
                            className="w-full border rounded p-2 min-h-[100px]"
                            placeholder="You can leave reviews about this website"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            Ratings
                        </label>
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setRating(value)}
                                    className="text-2xl focus:outline-none"
                                >
                                    <StarIcon
                                        className={`h-6 w-6 ${value <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-gray-800 text-white px-6 py-2 rounded font-medium hover:bg-gray-700 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                </form>
            </div>
        </div>
    );
} 